/* eslint-disable react/destructuring-assignment */
import React, { FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ListingEntry } from 'graphql/listings/queries';
import { ChargeFormEntry } from '../../types';
import { PartiesInvolvedRow } from './components/PartiesInvolvedRow/PartiesInvolvedRow';
import { ChargeDocumentRow } from './components/ChargeDocumentRow';
import { ChargeRow } from './components/ChargeRow';
import { ChargesFormType } from '../../hooks';
import { MobileChargesCard } from '../MobileChargesCard';

interface Props {
	charge: ChargeFormEntry;
	index: number;
	currentLetter?: string;
	lettersPerMonth: string[];
	textPredectiveCharges: ListingEntry[];
	formatedAccountingData: ChargeFormEntry[];
	hideTax: boolean;
	isOrderTypeNotHUD1: boolean;
	isMobile: boolean;
	onDeleteCharge: (rowIndex: number) => void;
	onEditCharge: (charge: ChargeFormEntry, rowIndex: number) => void;
	onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
}

export const ChargesTableRow: FC<Props> = ({
  charge,
  onDeleteCharge,
  onEditCharge,
  onEditSplitAmount,
  index,
  isOrderTypeNotHUD1,
  currentLetter,
  lettersPerMonth,
  textPredectiveCharges,
  hideTax,
  formatedAccountingData,
  isMobile,
}) => {
  const [open, setOpen] = useState(false);

  const { setValue, getValues } = useFormContext<ChargesFormType>();

  const handleAmountFieldChange = useCallback(
    (newAmount: number | null) => {
      setValue(`charges.${index}.buyer_at`, 0, { shouldDirty: true });
      setValue(`charges.${index}.buyer_before`, 0, { shouldDirty: true });
      setValue(`charges.${index}.seller_at`, 0, { shouldDirty: true });
      setValue(`charges.${index}.seller_before`, 0, { shouldDirty: true });
      setValue(`charges.${index}.by_others`, 0, { shouldDirty: true });

      if (currentLetter === 'G' && newAmount !== null) {
        let perMonth: number = 0;
        const numberOfMonths = getValues(`charges.${index}.months`);

        if (newAmount !== 0 && numberOfMonths) {
          perMonth = newAmount / numberOfMonths;
        }
        setValue(`charges.${index}.per_month`, perMonth, { shouldDirty: true });
      }
    },
    [index, setValue, getValues, currentLetter]
  );

  const handlePaidByAmountChange = useCallback(() => {
    let newAmount =
			getValues(`charges.${index}.buyer_at`) +
			getValues(`charges.${index}.seller_at`) +
			getValues(`charges.${index}.by_others`);

    if (isOrderTypeNotHUD1) {
      newAmount =
				newAmount +
				getValues(`charges.${index}.buyer_before`) +
				getValues(`charges.${index}.seller_before`);
    }

    setValue(`charges.${index}.amount`, newAmount, { shouldDirty: false });

    if (currentLetter === 'G') {
      let perMonth: number = 0;
      const numberOfMonths = getValues(`charges.${index}.months`);

      if (newAmount !== 0 && numberOfMonths) {
        perMonth = newAmount / numberOfMonths;
      }
      setValue(`charges.${index}.per_month`, perMonth, { shouldDirty: true });
    }
  }, [index, setValue, getValues, currentLetter, isOrderTypeNotHUD1]);

  const handleNumberOfMontsFieldChange = useCallback(
    (newMonths: number) => {
      if (currentLetter === 'G') {
        const currentAmount = getValues(`charges.${index}.amount`);

        let newPerMonth: number = 0;
        let newBuyerAtAmount: number = 0;

        if (newMonths !== 0 && currentAmount !== 0) {
          newPerMonth = currentAmount / newMonths;
          newBuyerAtAmount = newPerMonth * newMonths;
        }

        setValue(`charges.${index}.per_month`, newPerMonth, { shouldDirty: true });
        setValue(`charges.${index}.buyer_at`, newBuyerAtAmount, { shouldDirty: true });
      }
    },
    [index, setValue, getValues, currentLetter]
  );

  const handlePerMonthAmountFieldChange = useCallback(
    (newPerMonth: number) => {
      if (currentLetter === 'G') {
        let newAmount: number = newPerMonth;
        const currentNumberOfMonths = getValues(`charges.${index}.months`);

        if (currentNumberOfMonths && currentNumberOfMonths !== 0) {
          newAmount = newPerMonth * currentNumberOfMonths;
        }

        setValue(`charges.${index}.amount`, newAmount, { shouldDirty: true });
      }
    },
    [index, setValue, getValues, currentLetter]
  );

  const handleToggleRowOpen = () => {
    setOpen((prevValue) => !prevValue);
  };

  if (isMobile) {
    return (
      <MobileChargesCard
        charge={charge}
        index={index}
        currentLetter={currentLetter}
        textPredectiveCharges={textPredectiveCharges}
        hideTax={hideTax}
        onDeleteCharge={onDeleteCharge}
        onEditCharge={onEditCharge}
        onEditSplitAmount={onEditSplitAmount}
        isPartiesOpen={open}
        onToggleParties={handleToggleRowOpen}
        onChargeAmountChange={handleAmountFieldChange}
        onChargePaidByAmountChange={handlePaidByAmountChange}
      />
    );
  }

  const chargeIsDocument = charge.code === 'document';

  return (
    <>
      {chargeIsDocument ? (
        <ChargeDocumentRow
          charge={charge}
          index={index}
          currentLetter={currentLetter}
          textPredectiveCharges={textPredectiveCharges}
          hideTax={hideTax}
          onDelete={onDeleteCharge}
          onEdit={onEditCharge}
          onEditSplitAmount={onEditSplitAmount}
          isPartiesInvolvedRowOpen={open}
          togglePartiesinvolvedRowOpen={handleToggleRowOpen}
          onChargeAmountChange={handleAmountFieldChange}
          onChargePaidByAmountChange={handlePaidByAmountChange}
        />
      ) : (
        <ChargeRow
          charge={charge}
          index={index}
          currentLetter={currentLetter}
          lettersPerMonth={lettersPerMonth}
          textPredectiveCharges={textPredectiveCharges}
          formatedAccountingData={formatedAccountingData}
          hideTax={hideTax}
          isOrderTypeNotHUD1={isOrderTypeNotHUD1}
          onDeleteCharge={onDeleteCharge}
          onEditCharge={onEditCharge}
          onEditSplitAmount={onEditSplitAmount}
          isPartiesInvolvedRowOpen={open}
          togglePartiesinvolvedRowOpen={handleToggleRowOpen}
          onChargeAmountChange={handleAmountFieldChange}
          onChargePaidByAmountChange={handlePaidByAmountChange}
          onChargeNumberOfMonthsChange={handleNumberOfMontsFieldChange}
          onPerMonthChange={handlePerMonthAmountFieldChange}
        />
      )}
      <PartiesInvolvedRow open={open} charge={charge} onEditParties={onEditSplitAmount} />
    </>
  );
};
